"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ReadOnlySelect = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _Input = require("../Input/Input");
var _ReadOnlySelect = require("./ReadOnlySelect.styles");
var _excluded = ["children", "defaultValue", "onChange", "readOnly", "value"];
/**
 * Use this instead of `Select` to display a read-only `select` element.
 *
 * The [`HTML spec`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly)
 * doesn't actually support read-only `select` elements so, when set to the read-only state,
 * the `select` is converted into a read-only text `input` and an accompanying hidden input.
 * The text input displays the label text while the hidden `input` holds `value`, `name`,
 * `id` and `ref`.
 *
 * @since 8.27.0
 */
var ReadOnlySelect = /*#__PURE__*/(0, _react.forwardRef)(function (_ref, forwardedRef) {
  var children = _ref.children,
    defaultValue = _ref.defaultValue,
    onChange = _ref.onChange,
    readOnly = _ref.readOnly,
    controlledValue = _ref.value,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  var _useState = (0, _react.useState)(defaultValue || ''),
    _useState2 = (0, _slicedToArray2.default)(_useState, 2),
    uncontrolledValue = _useState2[0],
    setUncontrolledValue = _useState2[1];
  var value = typeof controlledValue !== 'undefined' ? controlledValue : uncontrolledValue;
  var onInputChange = (0, _react.useCallback)(function (e) {
    var changed = e.target.value;
    setUncontrolledValue(changed);
    if (onChange) {
      onChange(changed);
    }
  }, [onChange]);
  if (readOnly) {
    // Create flat array of <option> elements so they're easier to loop through
    var options = _react.Children.map(children, function (child) {
      if (child.type === 'optgroup') {
        return _react.Children.map(child.props.children, function (option) {
          return option;
        });
      }
      return child;
    });

    // Iterate through our array to determine which option is the "selected" one
    var selectedOption;
    var firstOption;
    for (var i = 0; i < options.length; i += 1) {
      // An option's label text can comes from either its `label` prop or its
      // text node (`children`)
      var optionLabel = options[i].props.label || options[i].props.children;

      // An option's value can come from its `value` prop or its label
      var optionValue = options[i].props.value || optionLabel;
      var optionObj = {
        value: optionValue,
        label: optionLabel
      };

      // Store first option's info in case we need it later
      if (typeof firstOption === 'undefined' && !options[i].props.disabled) {
        firstOption = optionObj;
      }

      // If this is the selected option...
      if (optionValue === value) {
        selectedOption = optionObj;
        break;
      }
    }

    // If no value or defaultValue was given, default to the first option
    if (typeof selectedOption === 'undefined') {
      selectedOption = firstOption;
    }
    return /*#__PURE__*/_react.default.createElement(_react.Fragment, null, /*#__PURE__*/_react.default.createElement(_ReadOnlySelect.StyledReadOnlySelect, {
      forwardedAs: _Input.Input,
      readOnly: true,
      value: typeof selectedOption !== 'undefined' ? selectedOption.label : '',
      type: "text"
    }), /*#__PURE__*/_react.default.createElement("input", (0, _extends2.default)({
      type: "hidden",
      value: typeof selectedOption !== 'undefined' ? selectedOption.value : '',
      ref: forwardedRef
    }, rest)));
  }

  // If not read-only, return a normal `select` element
  return /*#__PURE__*/_react.default.createElement(_ReadOnlySelect.StyledReadOnlySelect, (0, _extends2.default)({
    onChange: onInputChange,
    ref: forwardedRef,
    value: value
  }, rest), children);
});
exports.ReadOnlySelect = ReadOnlySelect;
ReadOnlySelect.displayName = 'ReadOnlySelect';
ReadOnlySelect.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The default value when using this as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   */
  defaultValue: _propTypes.default.string,
  /**
   * The value when using this as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   */
  value: _propTypes.default.string,
  /**
   * `<option>` and `<optgroup>` nodes
   */
  children: _propTypes.default.node.isRequired,
  /**
   * Set the select as read-only.
   * Can be controlled by `FormField` context.
   */
  readOnly: _propTypes.default.bool,
  /**
   * A callback that is called with the selected option's value.
   */
  onChange: _propTypes.default.func
} : {};